import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Button, Card, Empty, InputNumber, Modal, Pagination, Radio, Space, Spin, Statistic, message } from "antd";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import AppConfig from "../../core/service/AppConfig";
import CardTitle from "../../core/components/CardTitle";
import LayoutWithSider from "../../core/components/layout/layout-with-sider/LayoutWithSider";
import DatePicker from "../../core/components/DatePicker";
import HospitalSelectBox from "../../app/hospital/HospitalSelectBox";
import DuplicateGroupCard from "../../app/report-billing/duplicates/DuplicateGroupCard";
import { LocalStorageService } from "../../core/service/local-storage-service";
import { DATE_OPTIONS, getBeginLocalStr, getBeginStr, getDateFromNow, getEndLocalStr, getEndStr } from "../../core/utils/date-utils";
import "../../app/report-billing/duplicates/duplicate-styles.css";

// Sunucudaki ReportBillingDateType ile birebir.
const DATE_TYPE = { CONFIRM: 0, REPORT: 1, STUDY: 2 };
const DEFAULT_THRESHOLD = 40;
const PAGE_SIZE = 20;

export default function ReportBillingDuplicatesPage() {
    const { t } = useTranslation();
    const location = useLocation();

    // Faturalandırma ekranındaki buton, o anki tarih aralığını ve hastane seçimini taşır.
    const incoming = location.state?.billingFilter;

    const [dateType, setDateType] = useState(incoming?.dateType ?? DATE_TYPE.CONFIRM);
    const [dates, setDates] = useState(() => {
        if (incoming?.dateBegin || incoming?.dateEnd)
            return { begin: incoming.dateBegin, end: incoming.dateEnd };

        const range = getDateFromNow(DATE_OPTIONS.LAST_15_DAYS);
        return { begin: range.start, end: range.end };
    });
    const [hospitalIdList, setHospitalIdList] = useState(incoming?.hospitalIdList || []);
    const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    // Seçim sayfalar arasında korunur: reportId → o raporun ekranda görülen faturalandırma durumu.
    // (expectedStatus olarak gider; arada değişen kayıtları sunucu atlar ve raporlar.)
    const selection = useRef(new Map());
    // Varsayılanı bir kez uygulanmış gruplar — kullanıcı seçimini bozmadan sayfaya dönebilsin.
    const initializedGroups = useRef(new Set());
    const [, forceRender] = useState(0);

    useEffect(() => {
        document.title = `${t('Duplicate Detection')} | ${AppConfig.getRisTitle()}`
    }, []);

    const buildInput = (page) => {
        const isLocal = dateType === DATE_TYPE.STUDY;

        return {
            filter: {
                dateType,
                dateBegin: isLocal ? getBeginLocalStr(dates.begin) : getBeginStr(dates.begin),
                dateEnd: isLocal ? getEndLocalStr(dates.end) : getEndStr(dates.end),
                hospitalIdList,
            },
            thresholdMinutes: threshold,
            paginationQuery: { pageNumber: page, pageSize: PAGE_SIZE },
        };
    }

    const applyDefaults = (groups) => {
        groups.forEach(group => {
            if (initializedGroups.current.has(group.key))
                return;

            initializedGroups.current.add(group.key);

            // Korunacak rapor işaretsiz, diğerleri seçili gelir.
            group.reports
                .filter(x => !x.isKeeper)
                .forEach(x => selection.current.set(x.reportId, x.billingStatus ?? 0));
        });
    }

    const detect = async (page = 1, keepSelection = false) => {
        if (!keepSelection) {
            selection.current = new Map();
            initializedGroups.current = new Set();
        }

        setLoading(true);
        const response = await ApiService.ReportBilling.DetectDuplicates(buildInput(page));
        setLoading(false);

        if (!response.success)
            return;

        applyDefaults(response.data?.groups || []);
        setResult(response.data);
        setPageNumber(page);
    }

    const toggleReport = (report, checked) => {
        if (checked)
            selection.current.set(report.reportId, report.billingStatus ?? 0);
        else
            selection.current.delete(report.reportId);

        forceRender(x => x + 1);
    }

    const toggleGroup = (group, checked) => {
        group.reports.forEach(report => {
            if (checked && !report.isKeeper)
                selection.current.set(report.reportId, report.billingStatus ?? 0);
            else
                selection.current.delete(report.reportId);
        });

        forceRender(x => x + 1);
    }

    const markSelected = () => {
        const items = Array.from(selection.current.entries())
            .map(([reportId, expectedStatus]) => ({ reportId, expectedStatus }));

        if (items.length === 0) {
            message.info(t('DUPLICATE_SELECTION_EMPTY'));
            return;
        }

        Modal.confirm({
            title: t('Mark As Duplicate'),
            content: t('DUPLICATE_BULK_CONFIRM', { count: items.length }),
            okText: t('OK'),
            cancelText: t('Cancel'),
            onOk: async () => {
                const response = await ApiService.ReportBilling.MarkDuplicates({ items });

                if (!response.success)
                    return;

                const skipped = response.data?.skipped?.length || 0;

                if (skipped > 0)
                    message.warning(t('BILLING_BULK_RESULT_SKIPPED', { updated: response.data.updated, skipped }));
                else
                    message.success(t('BILLING_BULK_RESULT', { updated: response.data?.updated || 0 }));

                if (response.data?.logWriteFailed)
                    message.error(t('BILLING_LOG_WRITE_FAILED'));

                // İşaretlenenler aday kümesinden çıkar; tarama baştan çalışır.
                await detect(1, false);
            },
        });
    }

    const selectedCount = selection.current.size;
    const groups = result?.groups || [];
    // Render başına bir kez: her kart için ayrı Set kurmak gereksiz kopya demek.
    const selectedIds = new Set(selection.current.keys());

    // Filtre çubuğu satır içinde durduğu için yan panel verilmiyor; LayoutWithSider yine
    // kullanılır çünkü sayfa çerçevesini (üst/alt/yan boşluklar, sabit başlığın altı) o kuruyor.
    // sidebarComponent verilmeyince şerit 0 genişliğe çöker (bkz. AppLogViewerPage).
    return (
        <LayoutWithSider fullWidth={true}>
        <Card title={<CardTitle title={t('Duplicate Detection')} />}>
            <Alert type={'info'} showIcon message={t('DUPLICATE_HINT')} style={{ marginBottom: 12 }} />

            <Space wrap align={'end'} style={{ marginBottom: 12 }}>
                <Radio.Group
                    size={'small'}
                    value={dateType}
                    onChange={(e) => setDateType(e.target.value)}
                    options={[
                        { label: t('Confirm Date Filter'), value: DATE_TYPE.CONFIRM },
                        { label: t('Report Date'), value: DATE_TYPE.REPORT },
                        { label: t('Study Date'), value: DATE_TYPE.STUDY },
                    ]}
                />

                <DatePicker
                    locale={LocalStorageService.getAntLocale()}
                    value={dates.begin}
                    showToday={false}
                    onChange={(date) => setDates({ ...dates, begin: date })}
                />
                <DatePicker
                    locale={LocalStorageService.getAntLocale()}
                    value={dates.end}
                    showToday={false}
                    onChange={(date) => setDates({ ...dates, end: date })}
                />

                <div style={{ minWidth: 260 }}>
                    <HospitalSelectBox
                        style={{ width: '100%' }}
                        value={hospitalIdList}
                        onChange={setHospitalIdList}
                    />
                </div>

                <Space size={4}>
                    <span style={{ fontSize: 12 }}>{t('Threshold Minutes')}</span>
                    <InputNumber
                        size={'small'}
                        min={1}
                        max={1440}
                        value={threshold}
                        onChange={(value) => setThreshold(value || DEFAULT_THRESHOLD)}
                        style={{ width: 80 }}
                    />
                </Space>

                <Button type={'primary'} loading={loading} onClick={() => detect(1, false)}>
                    {t('Detect Duplicates')}
                </Button>
            </Space>

            {result && (
                <>
                    <Space size={'large'} wrap style={{ marginBottom: 12 }}>
                        <Statistic title={t('Groups')} value={result.groupCount} valueStyle={{ fontSize: 18 }} />
                        <Statistic title={t('Reports')} value={result.reportCount} valueStyle={{ fontSize: 18 }} />
                        <Statistic title={t('Markable')} value={result.markableCount} valueStyle={{ fontSize: 18 }} />
                        <Statistic title={t('Candidates')} value={result.candidateCount} valueStyle={{ fontSize: 18 }} />
                        <Button
                            type={'primary'}
                            danger
                            disabled={selectedCount === 0}
                            onClick={markSelected}
                        >
                            {t('Mark As Duplicate')}{selectedCount > 0 ? ` (${selectedCount})` : ''}
                        </Button>
                    </Space>

                    <Spin spinning={loading}>
                        {groups.length === 0
                            ? <Empty description={t('DUPLICATE_NO_RESULT')} />
                            : groups.map(group => (
                                <DuplicateGroupCard
                                    key={group.key}
                                    group={group}
                                    threshold={result.thresholdMinutes}
                                    selectedIds={selectedIds}
                                    onToggleReport={toggleReport}
                                    onToggleGroup={toggleGroup}
                                />
                            ))}
                    </Spin>

                    {result.groupCount > PAGE_SIZE && (
                        <Pagination
                            style={{ marginTop: 12, textAlign: 'right' }}
                            current={pageNumber}
                            pageSize={PAGE_SIZE}
                            total={result.groupCount}
                            showSizeChanger={false}
                            // Seçim sayfalar arasında korunur; yeni sayfanın grupları varsayılanlarıyla eklenir.
                            onChange={(page) => detect(page, true)}
                        />
                    )}
                </>
            )}

            {!result && !loading && (
                <Empty description={t('DUPLICATE_NOT_RUN')} />
            )}
        </Card>
        </LayoutWithSider>
    )
}
